import React from "react";
import { Link } from "react-router-dom";
import { Shield, Mail, MapPin } from "lucide-react";

/**
 * Static Privacy Policy for EasyStay Zambia. Kept as inline JSX (not stored in
 * MongoDB like the T&Cs) since content changes here should go through code review.
 */
export default function Privacy() {
  const updated = "August 2026";
  return (
    <div className="mx-auto max-w-3xl px-6 py-12 lg:px-8" data-testid="privacy-page">
      <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-copper">
        <Shield className="h-3.5 w-3.5" /> Legal
      </div>
      <h1 className="mt-2 font-display text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">Privacy Policy</h1>
      <p className="mt-2 text-sm text-slate-500">EasyStay Zambia · easystayzm.com · Last updated {updated}</p>

      <div className="prose prose-slate mt-8 max-w-none text-slate-700">
        <p>EasyStay (&ldquo;we&rdquo;, &ldquo;us&rdquo;, &ldquo;easystayzm.com&rdquo;) is committed to protecting the privacy of guests and hosts using our marketplace for short-stay accommodation in Zambia. This policy explains what personal information we collect, how we use it, and the choices you have.</p>

        <h2 className="font-display text-xl font-bold text-slate-900">1. Information we collect</h2>
        <ul>
          <li><b>Account data</b>: name, email, phone number, and (optionally) a Google profile if you sign in with Google.</li>
          <li><b>Identity verification</b>: a government-issued ID photo and a selfie, used one time to confirm you are the account holder. Uploaded to encrypted object storage.</li>
          <li><b>Bookings & payments</b>: property, dates, key preference, mobile-money phone number, or Stripe card token. We never store raw card details.</li>
          <li><b>Reviews & favourites</b>: star ratings and comments you post, and properties you heart.</li>
          <li><b>Device data</b>: browser, coarse city (for the &ldquo;Near you&rdquo; section — you can decline), and non-identifying analytics.</li>
        </ul>

        <h2 className="font-display text-xl font-bold text-slate-900">2. How we use it</h2>
        <ul>
          <li>Match guests and hosts, process bookings, deliver digital keys via SMS (Twilio).</li>
          <li>Prevent fraud (KYC + brute-force lockout on login).</li>
          <li>Send booking confirmations, host notifications, and — with your consent — weekly reports to admins.</li>
          <li>Improve product quality using aggregated, non-identifying trends.</li>
        </ul>

        <h2 className="font-display text-xl font-bold text-slate-900">3. SMS opt-out</h2>
        <p>Reply <b>STOP</b> to any EasyStay SMS to unsubscribe from all transactional and marketing messages. Reply <b>START</b> to opt back in. Reply <b>HELP</b> for support.</p>

        <h2 className="font-display text-xl font-bold text-slate-900">4. Third parties we share with</h2>
        <ul>
          <li><b>Stripe</b> — card payments (US/EU processors).</li>
          <li><b>Twilio</b> — SMS delivery for digital keys and booking confirmations.</li>
          <li><b>Emergent</b> — email delivery (booking receipts, admin reports) and Google Sign-in.</li>
          <li><b>MTN / Airtel / Zamtel</b> — mobile-money settlement (currently mocked in the app).</li>
        </ul>
        <p>We do not sell your personal information.</p>

        <h2 className="font-display text-xl font-bold text-slate-900">5. Retention</h2>
        <p>Account, booking, and review data are retained while your account is active and up to 7 years thereafter for tax and fraud-prevention purposes. KYC selfies are retained for 24 months.</p>

        <h2 className="font-display text-xl font-bold text-slate-900">6. Your rights</h2>
        <p>You can access, correct, or delete your data by contacting us. If you close your account, we anonymise your reviews (name replaced with &ldquo;A guest&rdquo;) but keep the star rating for host reputation.</p>

        <h2 className="font-display text-xl font-bold text-slate-900">7. Contact</h2>
        <p className="flex items-center gap-2 text-slate-800"><Mail className="h-4 w-4 text-copper" /> <a href="mailto:privacy@easystayzm.com" className="text-copper hover:underline">privacy@easystayzm.com</a></p>
        <p className="flex items-center gap-2 text-slate-800"><MapPin className="h-4 w-4 text-copper" /> EasyStay Zambia · Lusaka · easystayzm.com</p>
      </div>

      <div className="mt-10 flex flex-wrap gap-2 text-sm">
        <Link to="/trust-safety" data-testid="privacy-trust-link" className="btn-outline">Trust &amp; Safety</Link>
        <Link to="/terms/guest" className="btn-outline">Guest Terms</Link>
        <Link to="/terms/host" className="btn-outline">Host Agreement</Link>
        <Link to="/contact" className="btn-outline">Contact us</Link>
      </div>
    </div>
  );
}
